import {
  ImportHandler,
  ImportResult,
  asErrorResult,
  asLoadableResult,
  asOkayResult,
  ConfigResult,
  LoadableVolumeResult,
  LoadableResult,
  ErrorResult,
  ImportDataSourcesResult,
  asIntermediateResult,
  StateFileSetupResult,
  isVolumeResult,
} from '@/src/io/import/common';
import {
  DataSource,
  ChunkSource,
  findStateFileLeaves,
  getDataSourceName,
} from '@/src/io/import/dataSource';
import handleDicomFile from '@/src/io/import/processors/handleDicomFile';
import extractArchive from '@/src/io/import/processors/extractArchive';
import extractArchiveTarget from '@/src/io/import/processors/extractArchiveTarget';
import handleAmazonS3 from '@/src/io/import/processors/handleAmazonS3';
import handleGoogleCloudStorage from '@/src/io/import/processors/handleGoogleCloudStorage';
import importSingleFile from '@/src/io/import/processors/importSingleFile';
import handleRemoteManifest from '@/src/io/import/processors/remoteManifest';
import {
  restoreStateFile,
  completeStateFileRestore,
} from '@/src/io/import/processors/restoreStateFile';
import updateFileMimeType from '@/src/io/import/processors/updateFileMimeType';
import handleConfig, {
  InvalidConfigError,
} from '@/src/io/import/processors/handleConfig';
import {
  applyPreStateConfig,
  applyPostStateConfig,
} from '@/src/io/import/configJson';
import updateUriType from '@/src/io/import/processors/updateUriType';
import openUriStream from '@/src/io/import/processors/openUriStream';
import downloadStream from '@/src/io/import/processors/downloadStream';
import handleDicomStream from '@/src/io/import/processors/handleDicomStream';
import { FILE_EXT_TO_MIME } from '@/src/io/mimeTypes';
import { asyncSelect } from '@/src/utils/asyncSelect';
import { evaluateChain, Skip } from '@/src/utils/evaluateChain';
import { ensureError, partition } from '@/src/utils';
import { Chunk } from '@/src/core/streaming/chunk';
import { useDatasetStore } from '@/src/store/datasets';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useMessageStore } from '@/src/store/messages';
import { useCurrentImage } from '@/src/composables/useCurrentImage';

const unhandledResource: ImportHandler = (dataSource) => {
  return asErrorResult(new Error('Failed to handle resource'), dataSource);
};

const handleCollections: ImportHandler = (dataSource) => {
  if (dataSource.type !== 'collection') return Skip;
  // Propagate stateFileLeaf to sources so DICOM volumes can be mapped back to state file datasets
  const sources = dataSource.stateFileLeaf
    ? dataSource.sources.map((src) => ({
        ...src,
        stateFileLeaf: dataSource.stateFileLeaf,
      }))
    : dataSource.sources;
  return asIntermediateResult(sources);
};

function isSelectable(result: ImportResult): result is LoadableVolumeResult {
  return result.type === 'data' && result.dataType === 'image';
}

const applyConfigsPostState = (
  results: Array<ConfigResult>,
  imageIds: string[],
  replaceBorrowed: boolean
): (ConfigResult | ErrorResult)[] =>
  results.map((result) => {
    try {
      applyPostStateConfig(result.config, imageIds, { replaceBorrowed });
      return result;
    } catch (err) {
      return asErrorResult(ensureError(err), result.dataSource);
    }
  });

// The restore-time stateID -> storeID map: every state-file leaf a loadable
// covers maps to its ONE store id. Many-to-one is the normal shape — a merged
// multi-file DICOM volume covers every member file's per-file dataset id.
export function buildStateIDToStoreID(
  loadables: readonly LoadableResult[]
): Record<string, string> {
  const stateIDToStoreID: Record<string, string> = {};
  loadables.forEach((loadable) => {
    findStateFileLeaves(loadable.dataSource).forEach((leaf) => {
      stateIDToStoreID[leaf.stateID] = loadable.dataID;
    });
  });
  return stateIDToStoreID;
}

async function importDicomChunkSources(sources: ChunkSource[]) {
  if (sources.length === 0) return [];

  const volumeChunks = await useDICOMStore().importChunks(
    sources.map((src) => src.chunk)
  );

  // this is used to reconstruct the ChunkSource list
  const chunkToDataSource = new Map<Chunk, ChunkSource>();
  sources.forEach((src) => {
    chunkToDataSource.set(src.chunk, src);
  });

  return Object.entries(volumeChunks).map(([id, chunks]) =>
    asLoadableResult(
      id,
      {
        type: 'collection',
        sources: chunks.map((chunk) => chunkToDataSource.get(chunk)!),
      },
      'image'
    )
  );
}

type ImportPolicy =
  | {
      kind: 'application';
      restore: RestoreProcessors;
      standaloneImageIds: string[];
    }
  | { kind: 'volume-data' };

/**
 * The two halves of a state-file restore: the handler that reads the manifest
 * out of the pipeline, and the step that applies it once the bases have
 * loaded.
 */
export type RestoreProcessors = {
  restoreStateFile: typeof restoreStateFile;
  completeStateFileRestore: typeof completeStateFileRestore;
};

const hasDataIntent = (result: ImportResult) => {
  if (
    result.type === 'config' ||
    (result.type === 'error' && result.error instanceof InvalidConfigError)
  )
    return false;
  if (result.type !== 'intermediate') return true;
  return (
    result.dataSources.length === 0 ||
    result.dataSources.some((source) => source.type === 'chunk')
  );
};

export const appRestoreProcessors = (): RestoreProcessors => ({
  restoreStateFile,
  completeStateFileRestore,
});

async function completeStateFileSetups(
  stateFileSetups: StateFileSetupResult[],
  restore: RestoreProcessors,
  loadableResults: LoadableResult[],
  failedLeaves: NonNullable<
    Parameters<RestoreProcessors['completeStateFileRestore']>[3]
  >['failedLeaves']
) {
  const stateIDToStoreID = buildStateIDToStoreID(loadableResults);
  // Leaf stateIDs covered by a consolidated notice that actually ran: only
  // their errors may be suppressed by the import pipeline.
  const reportedStateIDs = new Set<string>();
  for (const setup of stateFileSetups) {
    try {
      await restore.completeStateFileRestore(
        setup.manifest,
        setup.stateFiles,
        stateIDToStoreID,
        {
          missingFiles: setup.missingFiles,
          failedLeaves,
          migration: setup.migration,
        }
      );
      setup.dataSources.forEach((src) => {
        findStateFileLeaves(src).forEach((leaf) =>
          reportedStateIDs.add(leaf.stateID)
        );
      });
      setup.missingFiles.forEach(({ stateID }) =>
        reportedStateIDs.add(stateID)
      );
    } catch (err) {
      // Auto-degrade to an ephemeral open: a mid-restore throw leaves the
      // already-loaded bases as plain datasets and the session's attached set
      // stays empty, so a later save prunes every launch-composition entry.
      // Restore steps already applied (layout, view bindings) are not rolled
      // back. One notice here; this setup's leaf errors stay unflagged so the
      // generic load-error dialog still names them.
      useMessageStore().addWarning(
        'Could not restore the saved session; opened its images instead',
        { details: ensureError(err).message }
      );
    }
  }
  return reportedStateIDs;
}

// Config imported with data targets its loaded images; standalone config keeps
// the image whose view started the import.
const configTargetImageIds = (
  importedData: boolean,
  loadables: readonly LoadableResult[],
  standaloneImageIds: string[]
) =>
  importedData
    ? loadables.filter(isVolumeResult).map(({ dataID }) => dataID)
    : standaloneImageIds;

async function importDataSourcesWithPolicy(
  dataSources: DataSource[],
  policy: ImportPolicy
): Promise<ImportDataSourcesResult[]> {
  const cleanupHandlers: Array<() => void> = [];
  const onCleanup = (fn: () => void) => {
    cleanupHandlers.push(fn);
  };
  const cleanup = () => {
    while (cleanupHandlers.length) cleanupHandlers.pop()!();
  };

  const importContext = {
    fetchFileCache: new Map<string, File>(),
    onCleanup,
    importDataSources: (sources: DataSource[]) =>
      importDataSourcesWithPolicy(sources, policy),
  };

  // State files first: a manifest's top-level `segments` is also a config key.
  const applicationHandlers =
    policy.kind === 'application'
      ? [policy.restore.restoreStateFile, handleConfig, handleRemoteManifest]
      : [];

  const handlers = [
    handleCollections,

    openUriStream,

    // updating the file/uri type should be first step in the pipeline
    updateFileMimeType,
    updateUriType,
    // before extractArchive as .zip extension is part of state file check
    ...applicationHandlers,
    handleGoogleCloudStorage,
    handleAmazonS3,

    // stream handling
    handleDicomStream,
    downloadStream,

    extractArchive,
    extractArchiveTarget,
    // should be before importSingleFile, since DICOM is more specific
    handleDicomFile, // collect DICOM files to import later
    importSingleFile,
    // catch any unhandled resource
    unhandledResource,
  ];

  const chunkSources: DataSource[] = [];
  const configResults: ConfigResult[] = [];
  const stateFileSetups: StateFileSetupResult[] = [];
  const results: ImportDataSourcesResult[] = [];
  let importedData = false;

  let queue = dataSources.map((src) => ({
    promise: evaluateChain(src, handlers, importContext),
    source: src,
  }));

  while (queue.length) {
    const { index } = await asyncSelect(queue.map((item) => item.promise));
    const { promise, source } = queue[index];
    const result = await promise.catch((err) => asErrorResult(err, source));
    importedData ||= hasDataIntent(result);
    queue = queue.filter((_, i) => i !== index);

    switch (result.type) {
      case 'stateFileSetup':
        stateFileSetups.push(result);
      // fallthrough to handle dataSources
      case 'intermediate': {
        const [chunks, otherSources] = partition(
          (ds) => ds.type === 'chunk',
          result.dataSources
        );
        chunkSources.push(...chunks);

        queue.push(
          ...otherSources.map((src) => ({
            promise: evaluateChain(src, handlers, importContext),
            source: src,
          }))
        );
        break;
      }
      case 'config':
        configResults.push(result);
        try {
          await applyPreStateConfig(result.config);
        } catch (err) {
          results.push(asErrorResult(ensureError(err), result.dataSource));
        }
        break;
      case 'ok':
      case 'error':
      case 'data':
        results.push(result);
        break;
      default:
        throw new Error(`Invalid result: ${result}`);
    }
  }

  cleanup();

  const dicomChunkSources = chunkSources.filter(
    (src): src is ChunkSource =>
      src.type === 'chunk' && src.mime === FILE_EXT_TO_MIME.dcm
  );

  try {
    const dicomResults = await importDicomChunkSources(dicomChunkSources);
    results.push(...dicomResults);
  } catch (err) {
    const errorSource =
      dicomChunkSources.length === 1
        ? dicomChunkSources[0]
        : ({ type: 'collection', sources: dicomChunkSources } as DataSource);
    results.push(asErrorResult(ensureError(err), errorSource));
  }

  const loadableResults = results.filter(
    (r): r is LoadableResult => r.type === 'data'
  );

  useDatasetStore().addDataSources(loadableResults);

  // Failed leaves (e.g. a 404'd uri member of a multi-leaf dataset) feed the
  // restore's consolidated notice: a dataset that still resolves from its
  // surviving leaves restores truncated and must say which sources failed.
  const failedLeaves = results
    .filter((r): r is ErrorResult => r.type === 'error')
    .flatMap((r) =>
      findStateFileLeaves(r.dataSource).map((leaf) => ({
        stateID: leaf.stateID,
        name: getDataSourceName(r.dataSource) ?? leaf.stateID,
      }))
    );

  let reportedStateIDs = new Set<string>();
  if (policy.kind === 'application') {
    reportedStateIDs = await completeStateFileSetups(
      stateFileSetups,
      policy.restore,
      loadableResults,
      failedLeaves
    );

    results.push(
      ...applyConfigsPostState(
        configResults,
        configTargetImageIds(
          importedData,
          loadableResults,
          policy.standaloneImageIds
        ),
        importedData
      )
    );
  }

  // A failed state-file leaf is already counted in the restore's consolidated
  // missing-content notice, so this layer owns its reporting: it returns as an
  // accounted-for 'ok' result, never as an error. An 'error' result in the
  // return value therefore ALWAYS means "not yet surfaced to the user" —
  // callers own reporting exactly the errors they receive, and no failure is
  // reported twice.
  return results.map((result) => {
    if (result.type !== 'error') return result;
    const leaves = findStateFileLeaves(result.dataSource);
    const covered =
      leaves.length > 0 &&
      leaves.every((leaf) => reportedStateIDs.has(leaf.stateID));
    return covered ? asOkayResult(result.dataSource) : result;
  });
}

export function importDataSources(
  dataSources: DataSource[],
  restore: RestoreProcessors = appRestoreProcessors()
): Promise<ImportDataSourcesResult[]> {
  const initiatingImageId = useCurrentImage('global').currentImageID.value;
  // Standalone config belongs to the initiating image, even if focus changes
  // while reading it. An explicit empty target updates only future defaults.
  return importDataSourcesWithPolicy(dataSources, {
    kind: 'application',
    restore,
    standaloneImageIds: initiatingImageId ? [initiatingImageId] : [],
  });
}

export function importVolumeDataSources(
  dataSources: DataSource[]
): Promise<ImportDataSourcesResult[]> {
  return importDataSourcesWithPolicy(dataSources, { kind: 'volume-data' });
}

export function toDataSelection(loadable: LoadableVolumeResult) {
  const { dataID } = loadable;
  return dataID;
}

export function convertSuccessResultToDataSelection(result: ImportResult) {
  if (!isSelectable(result)) return null;
  return toDataSelection(result);
}
